import { FC, useState } from 'react';
import {
  IAnswerView,
  IQuestionsView,
  ISurveyItemWithData,
  ISurveyResponseView,
  ISurveyView,
} from 'store/survey/surveyTypes';
import BlurModal from 'containers/Profile/Panels/DocumentManager/BlurModal';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ITagView } from 'store/providers/providerTypes';
import { ICompany } from 'store/companies/companiesTypes';
import Button from 'components/Button';
import { appDispatch } from 'store/store';
import { addResponse } from 'store/survey/surveySlice';
import notify from 'components/Notify';
import AnswerView from 'components/FileManager/ItemsViewer/Survey/SurveyResponseViewer/AnswerView';
import { QuestionBody, QuestionWrapper } from '../styles';
import TitleInput from '../TitleInput';
import { getTruthy } from 'utils/happyFunctions';
import Messages from './Messages';
import { convertNumbersToProperFormat } from 'utils/number';

interface ISurveyQuestionAnswerModalProps {
  isOpen: boolean;
  onClose: () => void;
  survey: ISurveyView;
  questions: IQuestionsView[];
  answers: { [key: number]: IAnswerView; error?: any };
  tags: {
    [key: number]: ITagView;
  };
  company?: ICompany;
  surveyItem?: ISurveyItemWithData;
}

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 100%;
  max-width: 100%;
  padding: 1rem 0.5rem;
  gap: 0.5rem;
`;

const QuestionAnswerContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const SurveyQuestionAnswerModal: FC<ISurveyQuestionAnswerModalProps> = ({
  isOpen,
  onClose,
  survey,
  questions,
  answers,
  tags,
  // company,
  surveyItem,
}) => {
  const { t } = useTranslation('survey');
  const [loading, setLoading] = useState<boolean>(false);

  const createFinalResponse = () => {
    return {
      draft: false,
      title: '',
      tags: Object.keys(tags || {}).map((el) => Number(el)),
      instance: surveyItem?.data?.id,
      surveyId: surveyItem?.data.surveyId,
      surveyAnswerList: Object.values(answers),
      effectiveDate: new Date().getTime(),
      surveyVersion: survey.currentVersion,
      extraInfo: {
        itemId: surveyItem?.data?.id,
        providerId: surveyItem?.data?.providerId,
      },
      extraOwnerTokens: surveyItem?.data?.extraOwnerTokens,
    } as ISurveyResponseView;
  };

  const handleSaveResponse = () => {
    if (surveyItem?.data) {
      const response = createFinalResponse();
      setLoading(true);
      appDispatch(
        addResponse({
          response,
          surveyItemData: surveyItem?.data,
          dontSendNotification: undefined,
          periodType: 'none',
        })
      ).then(() => {
        notify(t('savedSuccessfully'));
        setLoading(false);
        onClose();
      });
    }
  };

  const handleDismissResponse = () => {
    onClose();
  };

  return (
    <BlurModal
      isOpen={isOpen}
      onCloseModal={onClose}
      title={t('generatedAIQuestion')}
    >
      <ContentContainer>
        <QuestionAnswerContainer>
          {questions.map((question) => {
            // const QuestionComponent =
            //   QuestionsFrom?.[question.type as QuestionTypes];

            const answer = Object.values(answers).find(
              (el: IAnswerView) => el.questionId === question.id
            );
            const answerScore = getTruthy(answer.score, t('withoutScore'));

            return (
              <QuestionWrapper key={question.id} disabled={false}>
                <TitleInput
                  fields={question.fields || []}
                  hint={question.hint}
                  required={question.answerRequired}
                  title={question.title}
                  tags={question.tags}
                  allTags={tags}
                  badge={
                    typeof answerScore === 'number'
                      ? Messages.responseScore(
                          convertNumbersToProperFormat(answerScore)
                        )
                      : undefined
                  }
                  indexNumber={question.indexOrder}
                  survey={survey}
                />
                <QuestionBody>
                  <AnswerView
                    answer={answer}
                    question={question}
                    tags={tags}
                    dataContext={{}}
                    survey={survey}
                    surveyItem={surveyItem}
                  />
                </QuestionBody>
              </QuestionWrapper>
            );
            // const questionComponent = (
            //   <QuestionComponent
            //     disabled={true}
            //     key={question.id}
            //     tags={tags}
            //     answer={answer}
            //     isUnique={false} // for hide distinctive questions
            //     // onChange={() => {}}
            //     question={question}
            //     // distinctiveValues={distinctiveValues}
            //     isRequired={false}
            //     canChangeData={false}
            //     company={company}
            //     // currentUserCompanyRoles={currentUserCompanyRoles}
            //     isEditor={false}
            //     // onShowDataSourceModal={this.onShowDataSourceModal}
            //     randomizeOrder={false}
            //     survey={survey}
            //     dataSourceValues={{}}
            //     draft={false}
            //   />
            // );

            // return questionComponent;
          })}
        </QuestionAnswerContainer>
        <ButtonContainer>
          <Button
            color="red-outline"
            loading={loading}
            onClick={() => {
              // dissmiss answers
              handleDismissResponse();
            }}
          >
            {t('dismiss')}
          </Button>
          <Button
            color="green-outline"
            onClick={() => {
              // call save response handler
              handleSaveResponse();
            }}
            loading={loading}
          >
            {t('save')}
          </Button>
        </ButtonContainer>
      </ContentContainer>
    </BlurModal>
  );
};

export default SurveyQuestionAnswerModal;
